import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { logAuditAction } from '@/hooks/useAuditLog';

export const PLANNING_STATUSES = [
  { value: 'suggested', label: 'Sugerido', color: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30' },
  { value: 'planning', label: 'En Planificación', color: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30' },
  { value: 'in_development', label: 'En Desarrollo', color: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  { value: 'in_review', label: 'En Revisión', color: 'bg-purple-500/15 text-purple-700 dark:text-purple-300 border-purple-500/30' },
  { value: 'published', label: 'Publicado', color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  { value: 'archived', label: 'Archivado', color: 'bg-muted text-muted-foreground border-border' },
] as const;

export type PlanningStatus = typeof PLANNING_STATUSES[number]['value'];

export const PLANNED_FORMATS = [
  { value: 'video_animado_corto', label: 'Videos Animados Cortos' },
  { value: 'recurso_interactivo_caso_uso', label: 'Recursos Interactivos para Aplicar conceptos en un caso de uso en Html5' },
  { value: 'recurso', label: 'Recurso' },
  { value: 'clase_lia', label: 'Clase con Lía' },
  { value: 'lectura', label: 'Lectura' },
  { value: 'tutorial_paso_a_paso', label: 'Recursos Interactivos Tutorial Paso a Paso en Html5' },
  { value: 'simulador', label: 'Recursos Interactivos Simulador en html5' },
  { value: 'video_tutorial_aplicacion', label: 'Video Tutorial de Aplicación' },
  { value: 'video_presentador', label: 'Video Presentador' },
] as const;

export type PlannedFormat = typeof PLANNED_FORMATS[number]['value'];

export const PRIORITIES = [
  { value: 'baja', label: 'Baja', color: 'bg-slate-500/15 text-slate-700 dark:text-slate-300 border-slate-500/30' },
  { value: 'media', label: 'Media', color: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30' },
  { value: 'alta', label: 'Alta', color: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30' },
] as const;

export type Priority = typeof PRIORITIES[number]['value'];

export const VIGENCIA_OPTIONS = [
  { value: 'vigente', label: 'Vigente', color: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30' },
  { value: 'por_mejorar', label: 'Por Mejorar', color: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30' },
  { value: 'obsoleto', label: 'Obsoleto', color: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/30' },
] as const;

export type Vigencia = typeof VIGENCIA_OPTIONS[number]['value'];

export interface PlanningItem {
  id: string;
  name: string;
  description: string | null;
  category: string | null;
  content_type: string | null;
  planning_status: PlanningStatus;
  responsible: string | null;
  planned_format: PlannedFormat | null;
  priority: Priority | null;
  prospect_route: string | null;
  vigencia: Vigencia | null;
  observations: string | null;
  // Extended content fields
  duration: string | null;
  url: string | null;
  tags: string[] | null;
  access_level: string | null;
  thumbnail_url: string | null;
  author: string | null;
  vimeo_id: string | null;
  vimeo_uri: string | null;
  vimeo_player_url: string | null;
  html_content: string | null;
  target_levels: number[] | null;
  sort_order: number | null;
  created_at: string;
  updated_at: string;
}

const PLANNING_SELECT =
  'id, name, description, category, content_type, planning_status, responsible, planned_format, priority, prospect_route, vigencia, observations, duration, url, tags, access_level, thumbnail_url, author, vimeo_id, vimeo_uri, vimeo_player_url, html_content, target_levels, sort_order, created_at, updated_at';

export function usePlanningItems() {
  return useQuery({
    queryKey: ['content-planning'],
    queryFn: async (): Promise<PlanningItem[]> => {
      const { data, error } = await (supabase as any)
        .from('content_bank')
        .select(PLANNING_SELECT)
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return (data ?? []) as PlanningItem[];
    },
  });
}

export function useCreatePlanningItem() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (input: Partial<PlanningItem> & { name: string }) => {
      const payload: any = {
        name: input.name,
        description: input.description ?? null,
        category: input.category ?? null,
        content_type: input.content_type ?? null,
        planning_status: input.planning_status ?? 'suggested',
        responsible: input.responsible ?? null,
        planned_format: input.planned_format ?? null,
        priority: input.priority ?? 'media',
        prospect_route: input.prospect_route ?? null,
        vigencia: input.vigencia ?? 'vigente',
        observations: input.observations ?? null,
        duration: input.duration ?? null,
        url: input.url ?? null,
        tags: input.tags ?? null,
        access_level: input.access_level ?? 'free',
        thumbnail_url: input.thumbnail_url ?? null,
        author: input.author ?? null,
        vimeo_id: input.vimeo_id ?? null,
        vimeo_uri: input.vimeo_uri ?? null,
        vimeo_player_url: input.vimeo_player_url ?? null,
        html_content: input.html_content ?? null,
        target_levels: input.target_levels ?? [],
        sort_order: input.sort_order ?? 0,
      };
      const { data, error } = await (supabase as any)
        .from('content_bank')
        .insert(payload)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['content-planning'] });
      qc.invalidateQueries({ queryKey: ['content-bank'] });
      if (user) logAuditAction(user.id, 'content_create', `Contenido planificado creado: ${data.name}`, { content_id: data.id } as any);
    },
  });
}

export function useUpdatePlanningItem() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<PlanningItem> & { id: string }) => {
      const { data, error } = await (supabase as any)
        .from('content_bank')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['content-planning'] });
      qc.invalidateQueries({ queryKey: ['content-bank'] });
      if (user) logAuditAction(user.id, 'content_update', `Contenido planificado actualizado: ${data.name}`, { content_id: data.id } as any);
    },
  });
}

export function useChangePlanningStatus() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: PlanningStatus }) => {
      const { data, error } = await (supabase as any)
        .from('content_bank')
        .update({ planning_status: status })
        .eq('id', id)
        .select('id, name, planning_status')
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ['content-planning'] });
      qc.invalidateQueries({ queryKey: ['content-bank'] });
      qc.invalidateQueries({ queryKey: ['all-content-by-category'] });
      if (user) logAuditAction(user.id, 'content_update', `Estado de contenido: ${data.name} → ${data.planning_status}`, { content_id: data.id, planning_status: data.planning_status } as any);
    },
  });
}

export function useDeletePlanningItem() {
  const qc = useQueryClient();
  const { user } = useAuth();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('content_bank').delete().eq('id', id);
      if (error) throw error;
      return id;
    },
    onSuccess: (id) => {
      qc.invalidateQueries({ queryKey: ['content-planning'] });
      qc.invalidateQueries({ queryKey: ['content-bank'] });
      if (user) logAuditAction(user.id, 'content_delete', 'Contenido planificado eliminado', { content_id: id } as any);
    },
  });
}
